import { NotificationBadgeModel } from 'api-hooks/notification/model';
import { TransactionTabEnum } from 'api-hooks/transactions/common';
import { NavigationRoutes } from 'common/constants';
import { TransactionalStatusEnum } from 'common/constants/enum';
import Separator from 'components/common/separator';
import Tabs from 'components/common/tabs';
import { NotificationBadge } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

interface Props {
  setActiveKey: (value: React.SetStateAction<React.Key>) => void;
  activeKey: React.Key;
  setStatus: (value: React.SetStateAction<string | undefined>) => void;
  badge?: { [key: string]: NotificationBadgeModel };
}

export function useTransactionTabStatus() {
  const activeStatus = [TransactionalStatusEnum.Active].join('|');
  const needApprovalStatus = [
    TransactionalStatusEnum.VoidDraft,
    TransactionalStatusEnum.PendingVoidApproval,
    TransactionalStatusEnum.Draft,
    TransactionalStatusEnum.PendingApproval,
  ].join('|');
  const rejectedApprovalStatus = [
    TransactionalStatusEnum.RejectedApproval,
  ].join('|');
  const voidStatus = [TransactionalStatusEnum.Void].join('|');
  const completedStatus = [TransactionalStatusEnum.Completed].join('|');

  const allStatus = [
    activeStatus,
    needApprovalStatus,
    rejectedApprovalStatus,
    voidStatus,
    completedStatus,
  ].join('|');

  const tabStatus = {
    '0': allStatus,
    '1': activeStatus,
    '2': needApprovalStatus,
    '3': rejectedApprovalStatus,
    '4': voidStatus,
    '5': completedStatus,
  } as const;

  return {
    activeStatus,
    needApprovalStatus,
    rejectedApprovalStatus,
    voidStatus,
    completedStatus,
    allStatus,
    tabStatus,
  };
}

export default function TransactionListTabs(props: Props) {
  const { t } = useTranslation();
  const { activeKey, setActiveKey: setActivekey, setStatus, badge } = props;
  const { tabStatus } = useTransactionTabStatus();
  const { pathname } = useRouter();

  const isShowCompletedStatus = React.useMemo(() => {
    switch (pathname) {
      case NavigationRoutes.ReceiptsList:
        return true;
      default:
        return false;
    }
  }, [pathname]);

  return (
    <Tabs
      activeKey={activeKey}
      onChangeActive={({ activeKey }) => {
        setActivekey(activeKey);
        setStatus(tabStatus[activeKey]);
      }}
      items={[
        { title: t('common:all'), component: <></> },
        {
          title: (
            <>
              {t('common:active')}
              {!!badge?.[TransactionTabEnum.Active] &&
                !!badge?.[TransactionTabEnum.Active].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[TransactionTabEnum.Active].background_color,
                        color: badge?.[TransactionTabEnum.Active].color,
                      }}
                    >
                      {badge?.[TransactionTabEnum.Active].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:need_approval')}
              {!!badge?.[TransactionTabEnum.NeedApproval] &&
                !!badge?.[TransactionTabEnum.NeedApproval].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[TransactionTabEnum.NeedApproval]
                            .background_color,
                        color: badge?.[TransactionTabEnum.NeedApproval].color,
                      }}
                    >
                      {badge?.[TransactionTabEnum.NeedApproval].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:rejected_approval')}
              {!!badge?.[TransactionTabEnum.RejectedApproval] &&
                !!badge?.[TransactionTabEnum.RejectedApproval].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[TransactionTabEnum.RejectedApproval]
                            .background_color,
                        color:
                          badge?.[TransactionTabEnum.RejectedApproval].color,
                      }}
                    >
                      {badge?.[TransactionTabEnum.RejectedApproval].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        { title: t('common:void'), component: <></> },
        ...(isShowCompletedStatus
          ? [
              {
                title: t('status:completed'),
                component: <></>,
              },
            ]
          : []),
      ]}
      overrideStyles={{
        tabList: { minHeight: '0px' },
        tab: { minHeight: '0px', height: '0', flexGrow: '0' },
        tabPanel: { minHeight: '0px', height: '0', flexGrow: '0' },
      }}
    />
  );
}
